<script>
  import { SPRITESHEET_URL } from '../../utils/constants.ts';
  import BaseHeading from '../BaseHeading/BaseHeading.svelte';
  import BaseLink from '../BaseLink/BaseLink.svelte';
  import AppTextPromo from '../AppTextPromo/AppTextPromo.svelte';
  import { actions } from '../../utils/actions.ts';
  import { getPath } from '../../utils/imageKit.ts';
  import sanitizeClassName from '../../utils/sanitizeClassName';
  import { accessCode } from '../../stores/referrer';

  let {
    icon = undefined,
    caption = undefined,
    headingText = undefined,
    headingLevel = 'h2',
    content = undefined,
    link = undefined,
    action = undefined,
    appleLink = $bindable(undefined),
    appleIcon = undefined,
    googleLink = $bindable(undefined),
    googleIcon = undefined,
    appTextPromo = undefined,
    lazyloadImage = true,
    className = $bindable(''),
    smallerText = false,
    dataTestId = 'base-content-set',
    lang,
    inlineIconsAndText = false,
  } = $props();

  className = sanitizeClassName(className);
 
  if ($accessCode) {
    if (appleLink && appleLink.url) {
      const delimeter = appleLink.url.indexOf('?') !== -1 ? '&' : '?';
      appleLink.url = `${appleLink.url}${delimeter}source=${$accessCode}`;
    }

    if (googleLink && googleLink.url) {
      const delimeter = googleLink.url.indexOf('?') !== -1 ? '&' : '?';
      googleLink.url = `${googleLink.url}${delimeter}source=${$accessCode}`;
    }
  }

  const hasAppleIcon = appleIcon && appleIcon.url && appleIcon.alt && appleLink && appleLink.url;
  const hasGoogleIcon = googleIcon && googleIcon.url && googleIcon.alt && googleLink && googleLink.url;

  const googleIconUrl = getPath(googleIcon, { w: 160, h: 48 });
  const googleIconUrlHighRes = getPath(googleIcon, { w: 160, h: 48 }, 2);
  const appleIconUrl = getPath(appleIcon, { w: 160, h: 48 });
  const appleIconUrlHighRes = getPath(appleIcon, { w: 160, h: 48 }, 2);
</script>

{#if caption || headingText || content}
  <div class={className} data-testid={dataTestId}>
    {#if icon && icon.url && icon.alt}
      <div class="mb-6 h-28 w-auto">
        <img
          class="object-contain object-left h-full w-auto"
          loading={lazyloadImage === true ? 'lazy' : 'eager'}
          src={icon.url}
          alt={icon.alt}
        />
      </div>
    {/if}
    {#if caption}
      <div class="text-sm leading-normal text-blue-navy mb-2 lg:mb-4 md:text-xl md:leading-tight">{caption}</div>
    {/if}
    {#if headingText}
      <BaseHeading
        className={`leading-tight font-semibold text-blue-dark-navy text-lg ${smallerText ? 'md:text-xl lg:text-2xl' : 'md:text-2xl xl:text-3xl'}`}
        {headingLevel}
      >
        {headingText}
      </BaseHeading>
    {/if}
    {#if content}
      <div class={`mt-4 ${smallerText ? 'md:text-base' : 'md:text-lg'}`}>
        {@html content}
      </div>
    {/if}
    {#if link && link.text && link.url}
      <div class="mt-6 w-full">
        <BaseLink
          className="text-sm leading-normal font-semibold text-blue-cerulean md:text-xl hover-hover:hover:text-blue-darkish active:text-blue-darkish
          hover-hover:hover:underline active:underline group inline-flex justify-start flex-row w-auto"
          {...link}
        >
          <span>{link.text}</span>
          <svg
            class="ml-3 h-3 w-3 md:h-5 md:w-5 mt-1 md:mt-1.25 fill-current text-blue-cerulean
            group-hover:text-blue-darkish group-active:text-blue-darkish"
          >
            <use xlink:href={`${SPRITESHEET_URL}#right-arrow`} />
          </svg>
        </BaseLink>
      </div>
    {/if}
    {#if action && action.text && action.event}
      <div class="mt-6">
        <button
          class="leading-loose text-center font-semibold bg-blue-robin-egg hover-hover:hover:bg-blue-light active:bg-blue-malibu
          focus:outline-hidden focus:shadow-focus active:shadow-active rounded-md whitespace-nowrap min-w-25 px-8 flex
          items-center justify-center h-12 mt-0.5"
          onclick={() => {
            actions(action.event);
          }}
        >
          {action.text}
        </button>
      </div>
    {/if}

    {#if inlineIconsAndText}
      <div class="lg:flex lg:justify-between lg:-mt-8">
        {#if hasAppleIcon || hasGoogleIcon}
          <div class="mt-6 flex flex-row justify-start items-center">
            {#if hasAppleIcon}
              <BaseLink className="mr-4" {...appleLink}>
                <img
                  class="h-12"
                  loading={lazyloadImage === true ? 'lazy' : 'eager'}
                  width="144"
                  height="48"
                  src={appleIconUrl}
                  alt={appleIcon.alt}
                  srcset={`${appleIconUrl}, ${appleIconUrlHighRes} 2x`}
                  sizes="144px"
                />
              </BaseLink>
            {/if}
            {#if hasGoogleIcon}
              <BaseLink {...googleLink}>
                <img
                  class="h-12"
                  loading={lazyloadImage === true ? 'lazy' : 'eager'}
                  width="160"
                  height="48"
                  src={googleIconUrl}
                  alt={googleIcon.alt}
                  srcset={`${googleIconUrl}, ${googleIconUrlHighRes} 2x`}
                  sizes="160px"
                />
              </BaseLink>
            {/if}
          </div>
        {/if}
        {#if appTextPromo}
          <AppTextPromo {...appTextPromo} {lang} inlineIconsAndText="true" />
        {/if}
      </div>
    {:else}
      {#if hasAppleIcon || hasGoogleIcon}
        <div class="mt-4 flex flex-row justify-start items-center">
          {#if hasAppleIcon}
            <BaseLink className="mr-4" {...appleLink}>
              <img
                class="h-12"
                loading={lazyloadImage === true ? 'lazy' : 'eager'}
                width="144"
                height="48"
                src={appleIconUrl}
                alt={appleIcon.alt}
                srcset={`${appleIconUrl}, ${appleIconUrlHighRes} 2x`}
                sizes="144px"
              />
            </BaseLink>
          {/if}
          {#if hasGoogleIcon}
            <BaseLink {...googleLink}>
              <img
                class="h-12"
                loading={lazyloadImage === true ? 'lazy' : 'eager'}
                width="160"
                height="48"
                src={googleIconUrl}
                alt={googleIcon.alt}
                srcset={`${googleIconUrl}, ${googleIconUrlHighRes} 2x`}
                sizes="160px"
              />
            </BaseLink>
          {/if}
        </div>
      {/if}
      {#if appTextPromo}
        <AppTextPromo {...appTextPromo} {lang} />
      {/if}
    {/if}
  </div>
{/if}
